import React from 'react';
import { Card, List, Tag, Space, Typography, Button, Tooltip, Progress, Divider, Empty } from 'antd';
import {
  FileTextOutlined,
  NodeIndexOutlined,
  ArrowRightOutlined,
  PercentageOutlined,
  FireOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { AnnotationData } from '../../../types';

const { Text, Paragraph } = Typography;

interface SimilarCase {
  caseId: string;
  projectName: string;
  similarity: number;
  matchReason: string;
  topic: string;
  replyPosition: string;
  questionCount: number;
}

interface SimilarCasesProps {
  currentCaseId?: string;
  similarCases?: SimilarCase[];
  onViewCase?: (caseId: string) => void;
}

// 模拟相似案例数据
const mockSimilarCases: SimilarCase[] = [
  {
    caseId: 'CASE-2024-0089',
    projectName: '昆明某单位智慧消防系统建设项目',
    similarity: 92,
    matchReason: '资质条件争议 + 法规引用相似',
    topic: '资格条件',
    replyPosition: '不采纳',
    questionCount: 3,
  },
  {
    caseId: 'CASE-2024-0076',
    projectName: '大理州某医院消防改造工程',
    similarity: 85,
    matchReason: '技术参数争议 + 处理策略相似',
    topic: '技术参数',
    replyPosition: '部分采纳',
    questionCount: 2,
  },
  {
    caseId: 'CASE-2024-0065',
    projectName: '曲靖市某学校消防设备采购',
    similarity: 78,
    matchReason: '人员资格争议',
    topic: '人员要求',
    replyPosition: '澄清说明',
    questionCount: 2,
  },
  {
    caseId: 'CASE-2024-0054',
    projectName: '玉溪市某办公楼消防系统升级',
    similarity: 72,
    matchReason: '报价评审相关',
    topic: '报价评审',
    replyPosition: '不采纳',
    questionCount: 4,
  },
];

const SimilarCases: React.FC<SimilarCasesProps> = ({
  currentCaseId,
  similarCases = mockSimilarCases,
  onViewCase,
}) => {
  const getSimilarityColor = (score: number) => {
    if (score >= 90) return '#52c41a';
    if (score >= 80) return '#faad14';
    return '#8c8c8c';
  };

  const getPositionColor = (position: string) => {
    const colors: Record<string, string> = {
      '不采纳': 'red',
      '部分采纳': 'orange',
      '完全采纳': 'green',
      '澄清说明': 'blue',
    };
    return colors[position] || 'default';
  };

  return (
    <Card
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NodeIndexOutlined className="text-purple-500" />
            <span className="font-medium">相似案例推荐</span>
          </div>
          <Tag color="purple" icon={<PercentageOutlined />}>
            向量相似度
          </Tag>
        </div>
      }
     
      className="mb-4"
    >
      {similarCases.length === 0 ? (
        <Empty description="暂无相似案例" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
         
          dataSource={similarCases}
          renderItem={(item, index) => (
            <List.Item
              className="!px-0 !py-3 hover:bg-gray-50 rounded transition-colors"
            >
              <div className="w-full">
                {/* 头部：相似度 + 排名 */}
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-full text-white font-bold text-sm"
                    style={{ backgroundColor: getSimilarityColor(item.similarity) }}
                  >
                    {item.similarity}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FireOutlined
                        className={index < 3 ? 'text-red-500' : 'text-gray-300'}
                      />
                      <Text className="font-mono text-xs text-gray-400">
                        {item.caseId}
                      </Text>
                      {/* 查看按钮放到case编号旁 */}
                      <Button
                        type="link"
                       
                        icon={<ArrowRightOutlined />}
                        onClick={() => onViewCase?.(item.caseId)}
                        className="!px-1 !py-0 h-auto"
                      >
                        查看
                      </Button>
                    </div>
                    <Paragraph
                      ellipsis={{ rows: 1 }}
                      className="!mb-0 text-sm font-medium"
                    >
                      {item.projectName}
                    </Paragraph>
                  </div>
                </div>

                {/* 标签信息 */}
                <div className="flex items-center gap-2 flex-wrap ml-13">
                  <Tag color="orange" icon={<FileTextOutlined />}>
                    {item.topic}
                  </Tag>
                  <Tag color={getPositionColor(item.replyPosition)}>
                    <CheckCircleOutlined /> {item.replyPosition}
                  </Tag>
                  <Tag color="default">
                    {item.questionCount}个质疑
                  </Tag>
                </div>

                {/* 匹配原因 */}
                <div className="mt-2 ml-13">
                  <Text type="secondary" className="text-xs">
                    匹配: {item.matchReason}
                  </Text>
                </div>

                {/* 相似度进度条 */}
                <div className="mt-2 ml-13">
                  <Progress
                    percent={item.similarity}
                   
                    strokeColor={getSimilarityColor(item.similarity)}
                    showInfo={false}
                  />
                </div>
              </div>
            </List.Item>
          )}
        />
      )}

      <Divider className="my-3" />

      <div className="bg-gray-50 p-3 rounded text-xs text-gray-500">
        <div className="flex items-center gap-1 mb-2 font-medium">
          <PercentageOutlined />
          <span>相似度计算说明</span>
        </div>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>基于案例的质疑主题、答复策略、法规引用进行向量编码</li>
          <li>使用余弦相似度计算案例间的语义相似性</li>
          <li>推荐相似度 ≥ 70% 的案例作为参考</li>
        </ul>
      </div>
    </Card>
  );
};

export default SimilarCases;
